// FamilyAsk — Feb 19, 2026 (Randy Photo #2)
// "Ask me anything" section inside Family View for parent-role AND kid-role
// email collaborators. Wraps the existing /api/s/{slug}/ask endpoint (which
// supports authenticated owner/collaborator viewers even on hidden silos).
//
// Feb 24, 2026 (Randy pt89c-xxi) — AMA overhaul:
//   • Markdown rendering + auto-linkify of http/https/www URLs in answers
//   • Clear button LEFT of Send (clears the input textarea)
//   • Small centered "clear answer" chip inside the answer card
//   • Voice playback button (bottom-left of the answer card) — uses the
//     existing useVoiceQA `speakText` bridge (3× voucher / 1× BYOK), and
//     the VoiceLaneCTA is auto-opened when neither is funded (same CTA
//     the public Q&A pops — never a new modal).
//   • Description subtitle re-worded to be silo-type-agnostic (see the
//     themeManifest default: "Ask this silo anything.")
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatCircleText, PaperPlaneRight, CaretDown, CaretUp, X, SpeakerHigh, Stop } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";
import { useVoiceQA } from "@/lib/useVoiceQA";
import { linkifyUrls } from "@/lib/linkify";
import VoiceLaneCTA from "@/components/VoiceLaneCTA";

// Hoisted so React doesn't rebuild the component on every render.
const _MdAnchor = ({ node, ...props }) => (
  <a {...props} target="_blank" rel="noopener noreferrer" className="text-[#8A6AC1] underline underline-offset-2 hover:text-[#6B4EA6] break-words" />
);
const MD_COMPONENTS = { a: _MdAnchor };

export default function FamilyAsk({ siloSlug, siloId, defaultOpen = true }) {
  const tTitle       = useThemeLabel(T.THEME_KEY_FAMILYVIEW_ASK_TITLE);
  const tSubtitle    = useThemeLabel(T.THEME_KEY_FAMILYVIEW_ASK_SUBTITLE);
  const tPlaceholder = useThemeLabel(T.THEME_KEY_FAMILYVIEW_ASK_PLACEHOLDER);
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(`mysilo_family_ask_open_${siloId}`); return v === null ? defaultOpen : v === "1"; }
    catch { return defaultOpen; }
  });
  const persistOpen = (v) => {
    setOpen(v);
    try { localStorage.setItem(`mysilo_family_ask_open_${siloId}`, v ? "1" : "0"); } catch { /* ignore */ }
  };

  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState("");
  // Feb 24, 2026 — pull `voice_ready` off the public silo doc so premium
  // voice attempts don't 400 with LLM_GUARD every time on unfunded silos.
  const [voiceReady, setVoiceReady] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/s/${siloSlug}`);
        if (alive) setVoiceReady(!!r.data?.silo?.voice_ready);
      } catch { /* silent — voice-not-ready is the safe default */ }
    })();
    return () => { alive = false; };
  }, [siloSlug]);

  const [ctaOpen, setCtaOpen] = useState(false);
  const submit = async () => {
    if (!q.trim() || asking) return;
    setAsking(true); setAnswer("");
    try {
      const r = await api.post(`/s/${siloSlug}/ask`, { question: q.trim() });
      setAnswer(r.data?.answer || "Hmm, I couldn't come up with an answer.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ask failed. Try again.");
    } finally {
      setAsking(false);
    }
  };

  // Voice playback bridge (same hook the public Q&A uses).
  const getVoiceId = useCallback(() => undefined, []); // owner's default silo voice
  const getQuestionVoiceId = useCallback(() => undefined, []);
  const voice = useVoiceQA({
    slug: siloSlug,
    ask: async () => "",
    getVoiceId,
    getQuestionVoiceId,
    onGuardBlocked: () => toast.error("The silo owner is out of voice fuel — playback paused."),
    onSelfNoFunding: () => setCtaOpen(true),
    voiceReady,
    onScrollTick: undefined,
  });
  const isSpeaking = voice.state === "speaking";
  const onSpeakToggle = () => {
    if (!answer.trim()) return;
    if (isSpeaking) { voice.stopSpeaking?.(); return; }
    voice.speakText(answer, 0, "family-ask:answer");
  };

  const rendered = useMemo(() => linkifyUrls(answer || ""), [answer]);
  const clearAnswer = () => { setAnswer(""); voice.stopSpeaking?.(); };
  const clearInput = () => { setQ(""); };

  return (
    <section className="bg-white border-2 border-[#E9E2F2] rounded-2xl p-4" data-testid="familyview-ask-section">
      <button
        type="button"
        onClick={() => persistOpen(!open)}
        data-testid="familyview-ask-toggle"
        className={`w-full flex items-center gap-2 text-left ${open ? "mb-2" : ""}`}
        aria-expanded={open}
      >
        <ChatCircleText size={16} weight="duotone" className="text-[#8A6AC1]" />
        <h2 className="font-serif-display text-base text-[#1C1B1A] flex-1">{tTitle}</h2>
        {open ? <CaretUp size={12} weight="bold" className="text-[#6B6A68]" /> : <CaretDown size={12} weight="bold" className="text-[#6B6A68]" />}
      </button>
      {open && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-[11px] text-[#6B6A68] italic flex-1">{tSubtitle}</p>
            {answer && (
              <button
                type="button"
                onClick={clearAnswer}
                data-testid="familyview-ask-clear-desc"
                title="Clear the last answer"
                className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68] hover:text-[#1C1B1A] px-1.5 py-0.5 rounded border border-[#E9E2F2] hover:border-[#8A6AC1] flex items-center gap-0.5"
              >
                <X size={9} weight="bold" /> clear
              </button>
            )}
          </div>
          <div className="flex gap-1.5">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder={tPlaceholder}
              disabled={asking}
              data-testid="familyview-ask-input"
              className="flex-1 px-3 py-2 border-2 border-[#E9E2F2] rounded-lg text-sm bg-white focus:outline-none focus:border-[#8A6AC1]"
            />
            {/* Feb 24, 2026 — clear-input button, LEFT of Send per Randy. */}
            <button
              onClick={clearInput}
              disabled={asking || !q}
              title="Clear the question"
              data-testid="familyview-ask-clear-input"
              className="px-2.5 rounded-lg border-2 border-[#E9E2F2] text-[#6B6A68] hover:text-[#1C1B1A] hover:border-[#8A6AC1] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <X size={14} weight="bold" />
            </button>
            <button
              onClick={submit}
              disabled={asking || !q.trim()}
              data-testid="familyview-ask-send"
              className="px-3 rounded-lg bg-[#8A6AC1] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#7A5AB1] flex items-center justify-center"
            >
              <PaperPlaneRight size={15} weight="fill" />
            </button>
          </div>
          {asking && (
            <p className="text-xs text-[#6B6A68] mt-3 italic" data-testid="familyview-ask-loading">Thinking…</p>
          )}
          {answer && !asking && (
            <div className="mt-3 p-3 pb-8 rounded-lg bg-[#F8F5FC] border border-[#E9E2F2] text-sm text-[#1C1B1A] relative" data-testid="familyview-ask-answer">
              <div className="prose prose-sm max-w-none [&_p]:mb-2 [&_p:last-child]:mb-0 [&_a]:text-[#8A6AC1] [&_a]:underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_code]:bg-black/[0.06] [&_code]:px-1 [&_code]:rounded">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={MD_COMPONENTS}
                >
                  {rendered}
                </ReactMarkdown>
              </div>
              {/* Voice playback — bottom-left corner of the answer card. */}
              <button
                type="button"
                onClick={onSpeakToggle}
                data-testid="familyview-ask-speak"
                title={isSpeaking ? "Stop playback" : "Play answer aloud (premium voice)"}
                className={`absolute bottom-2 left-2 w-7 h-7 rounded-full border flex items-center justify-center transition ${
                  isSpeaking
                    ? "bg-[#8A6AC1] border-[#8A6AC1] text-white"
                    : "bg-white border-[#E9E2F2] text-[#8A6AC1] hover:border-[#8A6AC1] hover:bg-[#F0EAF7]"
                }`}
              >
                {isSpeaking ? <Stop size={12} weight="fill" /> : <SpeakerHigh size={12} weight="fill" />}
              </button>
              {/* Small centered clear-answer chip inside the card. */}
              <button
                type="button"
                onClick={clearAnswer}
                data-testid="familyview-ask-clear-answer"
                title="Clear the answer"
                className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.16em] text-[#6B6A68] hover:text-[#1C1B1A] px-2 py-0.5 rounded-full border border-[#E9E2F2] hover:border-[#8A6AC1] bg-white flex items-center gap-1"
              >
                <X size={9} weight="bold" /> clear
              </button>
            </div>
          )}
        </>
      )}
      <VoiceLaneCTA open={ctaOpen} variant="setup-funding" onClose={() => setCtaOpen(false)} />
    </section>
  );
}
